import React from 'react';
import PropTypes from 'prop-types';
import * as immutable from 'immutable';
import {List} from 'immutable';
import classNames from 'classnames';
import './TopicFormWords.styl';
import DeletableInput from 'components/pure/utils/DeletableInput';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import NewFilterWord from 'records/admin/topics/NewFilterWord';
import listAsString from 'utils/listAsString';
import GenericLink from 'components/GenericLink';
import AdminTopicEditorPageActions from 'actions/admin/AdminTopicEditorPageActions';

/**
 * List of topic words (alternate spellings), with editing and validation.
 */
export default class TopicFormWords extends React.PureComponent {
  static propTypes = {
    alternateSpellings: PropTypes.instanceOf(immutable.List),
    editable: PropTypes.bool.isRequired,
    editingNewTopic: PropTypes.bool,
    topic: PropTypes.instanceOf(NewFilterWord).isRequired,
    // map of words to a list of topics in which they appear. Used to check for duplicates.
    wordsToTopics: PropTypes.instanceOf(immutable.Map).isRequired,
  };

  constructor(props) {
    super(props);
    this.focusIndex = -1;
    this.inputRefs = [];
    this.state = {
      validationMessages: List(),
    };
  }

  setup() {
    this.setState({
      validationMessages: List(), // error message for each word. null means no error.
    });
  }

  addInputRef(idx, r) {
    this.inputRefs[idx] = r;
  }

  deleteAlternateSpelling(index) {
    const {validationMessages} = this.state;
    this.inputRefs.splice(index, 0); // remove the ref.
    // If we deleted the focused element, and it wasn't the last element, we should focus on the
    // previous element in the list so the user doesn't have to click.
    if (index === this.focusIndex && this.inputRefs.length !== 0) {
      // Switch focus to previous item, provided there is one.
      const newIndex = index === 0 ? 0 : index - 1;
      requestAnimationFrame(() => {
        const input = this.inputRefs[newIndex];
        input && input.focus();
      });
    }
    AdminTopicEditorPageActions.deleteAlternateSpellingAtIndex(index);
    this.setState({
      validationMessages: validationMessages.delete(index),
    });
  }

  updateAlternateSpelling(index, value) {
    const {validationMessages} = this.state;
    AdminTopicEditorPageActions.updateAlternateSpellingAtIndex(index, value);
    this.setState({
      validationMessages: validationMessages.set(index, this.validate(index, value)),
    });
  }

  /** Handle tabbing through the inputs. */
  onNext(index) {
    const {alternateSpellings} = this.props;
    const {validationMessages} = this.state;
    const newIndex = index + 1;
    // Add new (invalid) alternate spelling as needed.
    if (index === alternateSpellings.size - 1) {
      const newWord = ''; // Not valid. User will have to change.
      const validationError = this.validate(newIndex, newWord);
      AdminTopicEditorPageActions.appendNewAlternateSpelling(newWord);
      this.setState({
        validationMessages: validationMessages.set(newIndex, validationError),
      });
    }
    // Switch focus to next input.
    requestAnimationFrame(() => {
      const input = this.inputRefs[newIndex];
      input && input.focus();
    });
  }

  /**
   * Validate the alternateSpelling at the given index.
   *
   * @param index index of the word in the alternateSpellings list.
   * @param word the word (alternateSpelling) to validate.
   * @returns {array|null} a list of warning messages, if any apply, or null if the word is fine.
   */
  validate(index, word) {
    if (!word || word.trim() === '') {
      return null;
    }

    let validationMsgs = [];

    const {topic, wordsToTopics} = this.props;
    const matchingTopics = wordsToTopics.get(word.toLowerCase()) || List();
    const otherMatchingTopics = matchingTopics.filter(
      match => match.filterWordId !== topic.filterWordId
    );
    const numberOfOtherMatchingTopics = otherMatchingTopics.size;
    if (numberOfOtherMatchingTopics > 0) {
      const otherTopicNames = listAsString(otherMatchingTopics.map(t => t.word));
      const topicOrTopics = numberOfOtherMatchingTopics === 1 ? 'another topic' : 'other topics';
      validationMsgs.push(`Duplicated in ${topicOrTopics}: ${otherTopicNames}`);
    }

    if (this.props.alternateSpellings.find(w => w.toLowerCase() === word.toLowerCase())) {
      validationMsgs.push(
        'Duplicated in this topic. Topic level duplicate will be cleaned up upon save.'
      );
    }

    return validationMsgs.length === 0 ? null : validationMsgs;
  }

  componentDidMount() {
    this.setup();
  }

  render() {
    const {alternateSpellings, editable} = this.props;
    const {validationMessages} = this.state;
    return (
      <div className="topic-form-words">
        <div className="topic-form-words-header">Words / Phrases</div>
        <div className="topic-form-words-body">
          <div className="topic-form-words-flex-container">
            {alternateSpellings.map((alt, idx) => {
              const validationMessage = validationMessages.get(idx);
              return (
                <div
                  key={idx}
                  className={classNames('topic-word', {
                    warn: validationMessage,
                  })}>
                  {editable ? (
                    <GenericTooltip
                      tooltip={
                        validationMessage &&
                        validationMessage.length > 0 &&
                        validationMessage.map((vm, i) => <p key={i}>{vm}</p>)
                      }>
                      <DeletableInput
                        placeholder="Enter a word"
                        value={alt}
                        onNext={() => this.onNext(idx)}
                        onChange={value => {
                          this.updateAlternateSpelling(idx, value);
                        }}
                        onDelete={() => this.deleteAlternateSpelling(idx)}
                        onFocus={() => (this.focusIndex = idx)}
                        inputRef={r => this.addInputRef(idx, r)}
                      />
                    </GenericTooltip>
                  ) : (
                    alt
                  )}
                </div>
              );
            })}
          </div>
          {editable && (
            <div className="topic-form-words-add-link">
              <GenericLink onClick={() => this.onNext(alternateSpellings.size - 1)}>
                Add
              </GenericLink>
            </div>
          )}
        </div>
      </div>
    );
  }
}
